import AppGlobalsContext from "app/AppGlobalsContext";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import IconDeparting from "app/pages/.shared/IconDeparting";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import LocationMenuChip from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuChip";
import SmartDPDateCalendarContainer from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPDateCalendarContainer";
import {
	defaultValues,
	validateDepartureCity,
	validateSearch,
} from "app/pages/SmartDP/Search/SDPSearchForm/smartDPSearchFormSchema";
import SmartDPSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchInput";
import TravellersRoomButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomButton";
import { filterAndSortDepartureCities, isServerSide } from "app/utils/utils";
import { useDebouncedFuzzySearch } from "app/hooks";
import classNames from "classnames";
import { differenceInDays, format } from "date-fns";
import { Form, Formik } from "formik";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import PropTypes from "prop-types";
import { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { buildSdpRotationUri } from "app/features/SearchEngine/searchJourney/utils/buildSdpRotationUri";
import {
	DESTINATION_USA_CODE,
	FLAGSHIP_EVENT_KPIS,
	FS_QUOTE_EVENT_NAME,
	ICON_SIZES,
	SDP_ROTATION_DATE_FORMAT,
	SEARCH_FUZZY_THRESHOLD,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import CalendarDisabledView from "app/pages/SmartDP/Search/SDPSearchForm/CalendarDisabledView";
import TravellersRoomDrawerButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomDrawerButton";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { brandPropTypes } from "app/utils/propTypes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import InputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/InputValueFormat";
import TopOptionChip from "app/pages/SmartDP/Search/SDPSearchForm/TopOptionChip/TopOptionChip";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "app/pages/.shared/form/SearchForm.scss";
import "./SmartDPSearchForm.scss";
import LastSearchContainer from "app/features/SearchEngine/LastSearch/LastSearchContainer";
import ArrowDownIcon from "app/icons/navigation/ArrowDownIcon";
import { SearchJourney } from "app/features/SearchEngine/searchJourney/SearchJourney";
import FloatingDestinationCombobox from "app/pages/.shared/form/FloatingAdvancedSelectFormik/FloatingDestinationCombobox";
import { renderDestinationOption } from "app/features/SearchEngine/searchJourney/components/locationCombobox/LocationCombobox";

const SmartDPSearchForm = ({
	onSuccess = () => {},
	departureCities = [],
	destinations = [],
	cabins = [],
	initialValues = {},
	topDestinationsList = [],
	validateOnMount,
	usePortalForMenu,
	displayLoader,
	errorScrollIntoView = true,
	onFieldFocus = () => {},
	isAutoScrollActive,
	resetAllSDPProductsFilter = () => {},
}) => {
	const { sendHits } = useFlagship();
	const { track } = useAnalytics();
	const { formatMessage } = useIntl();

	const selectRefs = {
		departureCity: useRef(null),
		destinationResort: useRef(null),
		departureDate: useRef(null),
		cabin: useRef(null),
	};

	const [isTopDepartureCitiesMenuDisplayed, setIsTopDepartureCitiesMenuDisplayed] =
		useState(true);
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const handleSubmit = useCallback(
		(values, actions) => {
			resetAllSDPProductsFilter();
			actions.setSubmitting(false);
			onSuccess({ values, pathname });
		},
		[departureCities, destinations, cabins, pathname]
	);

	const [departureCityInput, setDepartureCityInput] = useState("");
	const departureCityFuzzyResults = useDebouncedFuzzySearch(departureCities, departureCityInput, {
		keys: ["label"],
		fuseOptions: { threshold: SEARCH_FUZZY_THRESHOLD },
	});
	const filteredDepartureCities = useMemo(
		() =>
			filterAndSortDepartureCities(
				departureCities,
				departureCityFuzzyResults,
				departureCityInput
			),
		[departureCities, departureCityFuzzyResults, departureCityInput]
	);

	// fix pour ne pas avoir les labels sur 2 lignes au premier rendu
	const mergeInitialValues = isServerSide
		? {
				occupancies: [{ adults: 2, children: 0, childrenBirthdates: [] }],
			}
		: {
				...defaultValues,
				...initialValues,
			};

	const isListingOrQuote = pathname.includes("/sdp/listing") || pathname === "/sdp/booking/quote";

	const isSticky =
		!isMobile && ["/sdp/listing", "/sdp/booking/quote"].some(path => pathname.includes(path));

	const hasCabins = isServerSide || cabins.length > 0;

	const topDepartureCities = orderBy(
		departureCities.filter(option => option?.isMain),
		["mainOrder"],
		["asc"]
	);

	const getLocationSelectionEventName = useCallback(
		(type, isTopSelection) => `form_click_${type}_${isTopSelection ? "popular" : "manual"}`,
		[]
	);

	const onHandleClickSearchButton = useCallback(() => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_QUOTE_EVENT_NAME.CLICK_ON_SEARCH_CTA,
		});
	}, []);

	const [refs, setRefs] = useState(null);

	const updateFloatingPositionReference = useCallback(refs => {
		setRefs(refs);
	}, []);

	const customFieldIds = ["departureDate"];

	const fieldIds = useMemo(() => ["departureCity", "destinationResort", "departureDate"], []);

	const fieldOrder = useMemo(
		() => fieldIds.map(id => ({ id, ref: selectRefs[id] })),
		[fieldIds, selectRefs]
	);
	const getFieldValue = (values, fieldId) => {
		const nestedFields = {
			departureDate: ["travelDates", "departureDate"],
		};

		if (nestedFields[fieldId]) {
			return nestedFields[fieldId].reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				values
			);
		}

		return values[fieldId];
	};

	const handleFieldChange = useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (value) {
				const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
				if (currentIndex === -1) {
					return;
				}

				const previousFields = fieldOrder.slice(0, currentIndex);
				const firstEmptyPrevField = previousFields.find(
					field => !getFieldValue(values, field.id)
				);

				if (firstEmptyPrevField && firstEmptyPrevField.ref.current) {
					if (customFieldIds.includes(firstEmptyPrevField.id)) {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.click();
						});
					} else {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.focus();
						});
					}
					return;
				}

				let nextIndex = currentIndex + 1;

				while (nextIndex < fieldOrder.length) {
					const nextField = fieldOrder[nextIndex];
					if (!getFieldValue(values, nextField.id) && nextField.ref.current) {
						requestAnimationFrame(() => {
							const event = new MouseEvent("click", {
								view: window,
								bubbles: true,
								cancelable: true,
							});
							nextField.ref.current.dispatchEvent(event);
							nextField.ref.current.click();
						});
						break;
					}
					nextIndex++;
				}
			}
		},
		[fieldOrder, customFieldIds]
	);

	const SEARCH_STEPS = useMemo(
		() => [
			{
				id: "departureCity",
				title: formatMessage({
					id: "sdp.search.departure.city.input.label",
				}),
			},
			{
				id: "destinationResort",
				title: formatMessage({ id: "sdp.search.destination.hotel.input.label" }),
			},
			{
				id: "travelDates",
				title: formatMessage({
					id: "sdp.search.departure.date.input.label",
				}),
			},
		],
		[formatMessage]
	);

	return (
		<div
			className={classNames("search-form search-form--sdp", {
				"search-form--sdp-with-cabins": hasCabins,
			})}
			ref={refs?.setPositionReference}
		>
			<Formik
				enableReinitialize
				initialValues={mergeInitialValues}
				validate={validateSearch(false)}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={
					validateOnMount && {
						departureCity: true,
						destinationResort: true,
						travelDates: {
							departureDate: true,
							endDate: true,
						},
						duration: {
							code: true,
						},
						occupancies: true,
						cabin: true,
					}
				}
				validateOnMount={validateOnMount}
			>
				{({ values, errors, isValid, isSubmitting, setFieldValue, submitCount }) => {
					const handleTravellersConfirmation = values => {
						setFieldValue("occupancies", values);
					};

					const departureCityCode = values?.departureCity?.code;
					const sdpRotationUri = buildSdpRotationUri(
						values?.destinationResort,
						departureCityCode
					);

					const hasLocationSelection = Boolean(
						values?.departureCity && values?.destinationResort
					);

					const destinationResortId = values?.destinationResort?.isHotel
						? values?.destinationResort?.places?.[0]?.id
						: values?.destinationResort?.code;
					const isUsaDestination = DESTINATION_USA_CODE.includes(destinationResortId);

					return (
						<Form
							className="search-form__form search-form--sdp"
							data-testid="sdp-search-form"
						>
							{isMobile && <SearchJourney travelMode="sdp" steps={SEARCH_STEPS} />}

							{!isMobile && (
								<>
									<SmartDPSearchInput
										id="departureCity"
										name="departureCity"
										usePortalForMenu={usePortalForMenu}
										selectRef={selectRefs.departureCity}
										openMenuOnFocus={
											!values.departureCity && topDepartureCities.length > 0
										}
										openMenuOnClick={
											!values.departureCity && topDepartureCities.length > 0
										}
										onChange={value => {
											if (value) {
												track(
													getLocationSelectionEventName(
														"departure_city",
														isTopDepartureCitiesMenuDisplayed
													),
													{
														locationLabel: value?.label,
													}
												);
											}
											handleFieldChange(
												"departureCity",
												setFieldValue,
												value,
												values
											);
										}}
										className="search-form__field search-form__field-departure"
										data-testid="departure-city-input"
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										validate={validateDepartureCity}
										label={
											<FormattedMessage id="sdp.search.departure.city.input.label" />
										}
										drawerInputLabel={
											<FormattedMessage id="sdp.search.departure.city.input.label" />
										}
										drawerTitleLabel={
											<FormattedMessage id="drawer.departure.title" />
										}
										components={{
											SingleValue: InputValueFormat,
											MenuList: isTopDepartureCitiesMenuDisplayed
												? LocationMenuChip
												: LocationMenuList,
										}}
										formatOptionLabel={({ label = "" }) => {
											if (isTopDepartureCitiesMenuDisplayed) {
												return <TopOptionChip label={label} />;
											}

											return (
												<div className="search-form__suggestion">
													<div className="search-form__suggestion-picto">
														<IconDeparting />
													</div>
													<Typography
														className="search-form__suggestion-first-label"
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
													>
														<span
															dangerouslySetInnerHTML={{
																__html: label,
															}}
														/>
													</Typography>
												</div>
											);
										}}
										getOptionValue={({ code }) => code}
										getOptionLabel={({ label }) => label}
										options={
											isTopDepartureCitiesMenuDisplayed
												? topDepartureCities
												: filteredDepartureCities
										}
										// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
										// valeur par defaut
										value={values?.departureCity || ""}
										noOptionsMessage={() => {
											return (
												<FormattedMessage id="sdp.search.no.result.label" />
											);
										}}
										popperWidth={360}
										popperOffset={[-16, 2]}
										locationMenuLabel={
											<FormattedMessage id="sdp.search.top.departure.cities.label" />
										}
										isTopLocationMenuDisplayed={
											isTopDepartureCitiesMenuDisplayed
										}
										onInputChange={inputValue => {
											if (isEmpty(inputValue)) {
												setIsTopDepartureCitiesMenuDisplayed(
													topDepartureCities.length > 0
												);
											} else if (isTopDepartureCitiesMenuDisplayed) {
												setIsTopDepartureCitiesMenuDisplayed(false);
											}
											setDepartureCityInput(inputValue);
										}}
										filterOption={() => true}
										loading={displayLoader}
										isFloating
										floatingConfig={{
											placement: "bottom-start",
											offset: ({ rects }) => ({
												mainAxis: -rects.reference.height - 16,
												crossAxis: -16,
											}),
										}}
										placeholder={
											<FormattedMessage id="sdp.search.departure.city.input.label.mobile" />
										}
										showPlaceholderOnlyOnFocus={true}
									/>

									<FloatingDestinationCombobox
										id="destinationResort"
										name="destinationResort"
										usePortalForMenu={usePortalForMenu}
										selectRef={selectRefs.destinationResort}
										renderDestinationOption={renderDestinationOption}
										onChange={(value, meta) => {
											handleFieldChange(
												"destinationResort",
												setFieldValue,
												value,
												values
											);

											if (value) {
												if (value?.isHotel) {
													track(
														getLocationSelectionEventName(
															"hotel",
															meta?.showChipMenu
														),
														{
															hotelName: value?.label,
															hotelId: value?.code,
															destinationPlaces: value?.places,
														}
													);
												} else {
													track(
														getLocationSelectionEventName(
															"destination",
															meta?.showChipMenu
														),
														{
															locationLabel:
																value?.label ||
																value?.labels?.slice(-1).join(", "),
														}
													);
												}
											}

											if (value?.label) {
												sendHits({
													type: HitType.EVENT,
													category: EventCategory.ACTION_TRACKING,
													action: FLAGSHIP_EVENT_KPIS.FLAG_SDP_FUNNEL_FLAGS_TRIGGERED,
													label: value?.label,
												});
											}
										}}
										className="search-form__field search-form__field-destination"
										data-testid="destination-resort-input"
										onFocus={onFieldFocus}
										label={
											<FormattedMessage id="sdp.search.destination.hotel.input.label" />
										}
										getOptionLabel={({ label }) => label}
										options={destinations}
										optionsWhenEmpty={topDestinationsList}
										value={values?.destinationResort || ""}
										loading={displayLoader}
										floatingConfig={{
											placement: "bottom-start",
											offset: ({ rects }) => ({
												mainAxis: -rects.reference.height - 16,
												crossAxis: -16,
											}),
										}}
									/>

									<SmartDPDateCalendarContainer
										id="travelDates"
										name="travelDates"
										data-testid="travel-date-input"
										selectRef={selectRefs.departureDate}
										hasLocationSelection={hasLocationSelection}
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										className="search-form__field search-form__field-calendar"
										updateFloatingPositionReference={
											updateFloatingPositionReference
										}
										popperOffset={[0, -58]}
										calendarPopoverWidth={isListingOrQuote ? 998 : 1024} // 1024 - 32 padding
										calendarDisabledView={
											<CalendarDisabledView
												mainMessage={
													values.departureCity &&
													values.destinationResort ? (
														<FormattedMessage id="sdp.calendar.unavailable.date.label" />
													) : (
														<FormattedMessage id="sdp.calendar.no.destination.no.departure.city.label" />
													)
												}
												secondaryMessage={
													values.departureCity &&
													values.destinationResort ? (
														<FormattedMessage id="sdp.calendar.unavailable.date.sublabel" />
													) : null
												}
											/>
										}
										uri={sdpRotationUri}
										handleDayChange={({
											durationsOfSelectedDepartureDate = [],
											selectedEndDate,
											selectedDepartureDate,
										}) => {
											// durationsOfSelectedDepartureDate [{duration, ed}]

											if (isEmpty(durationsOfSelectedDepartureDate)) {
												if (selectedEndDate && selectedDepartureDate) {
													const daysDifference = differenceInDays(
														new Date(selectedEndDate),
														new Date(selectedDepartureDate)
													);
													setFieldValue("duration", {
														code: daysDifference,
													});
												} else {
													setFieldValue("duration", {
														code: "",
													});
												}
											} else if (selectedEndDate) {
												const formattedSelectedEndDate = format(
													new Date(selectedEndDate),
													SDP_ROTATION_DATE_FORMAT
												);

												const dataOfSelectedDepartureDate =
													durationsOfSelectedDepartureDate.find(
														data => data.ed === formattedSelectedEndDate
													);
												if (dataOfSelectedDepartureDate) {
													setFieldValue("duration", {
														code: dataOfSelectedDepartureDate.duration,
													});
												} else {
													setFieldValue("duration", {
														code: "",
													});
												}
											} else {
												setFieldValue("duration", {
													code: "",
												});
											}
										}}
										loading={displayLoader}
									/>
								</>
							)}

							{isMobile ? (
								<TravellersRoomDrawerButton
									className={classNames(
										"search-form__field search-form__field-rooms",
										{
											"search-form__field--fullsize": !hasCabins,
										}
									)}
									id="travellersRooms"
									onConfirmation={handleTravellersConfirmation}
									initialValues={values.occupancies}
									label={
										<FormattedMessage id="sdp.search.travellers.input.label" />
									}
									validateOnMount={validateOnMount}
									warningMessage={isUsaDestination}
								/>
							) : (
								<TravellersRoomButton
									className={classNames(
										"search-form__field search-form__field-rooms",
										{
											"search-form__field--fullsize": !hasCabins,
										}
									)}
									id="travellersRooms"
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									onConfirmation={handleTravellersConfirmation}
									initialValues={values.occupancies}
									label={
										<FormattedMessage id="sdp.search.travellers.input.label" />
									}
									stickyMode={isSticky}
									validateOnMount={validateOnMount}
									loading={displayLoader}
									popperWidth={360}
									popperOffset={[80, 4]}
									warningMessage={isUsaDestination}
								/>
							)}

							{hasCabins && (
								<SmartDPSearchInput
									id="cabin"
									name="cabin"
									selectRef={selectRefs.cabin}
									openMenuOnFocus
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field search-form__field-cabin"
									data-testid="cabin-input"
									isSearchable={false}
									{...(isMobile && {
										label: (
											<FormattedMessage id="sdp.search.cabin.input.label" />
										),
									})}
									getOptionLabel={({ label = "", suffix = "" }) =>
										`${label}${suffix ? `, ${suffix}` : ""}`
									}
									components={{
										SingleValue: InputValueFormat,
									}}
									getOptionValue={({ code }) => code}
									options={cabins}
									value={cabins.find(({ code }) => {
										return code === get(values, "cabin.code");
									})}
									popperWidth={240}
									popperOffset={[-10, -40]}
									loading={displayLoader}
									{...(!isMobile && {
										inputValueIcon: <ArrowDownIcon size={ICON_SIZES.XS} />,
									})}
								/>
							)}

							{/* Do not display error message on validationOnMount (redirection from emirates) */}
							{!isValid && submitCount >= 1 && (
								<div className="search-form__error">
									<FormErrorMessages
										errors={errors}
										isSubmitting={isSubmitting}
										submitCount={submitCount}
										disableErrorsDetails={true}
										message={
											<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
												<FormattedMessage id="sdp.search.form.error.message" />
											</Typography>
										}
										scrollInToView={errorScrollIntoView}
									/>
								</div>
							)}

							<div className="search-form__footer">
								<Button
									variant={BUTTON_VARIANTS.FILLED}
									size={BUTTON_SIZES.LARGE}
									label={
										<FormattedMessage
											id={
												isListingOrQuote
													? "sdp.search.refresh.label"
													: "sdp.search.cta.label"
											}
										/>
									}
									submit
									className="search-form__button"
									loading={isSubmitting}
									data-testid="search-form-button"
									onClick={onHandleClickSearchButton}
									disabled={displayLoader || isSubmitting}
								/>

								<div className="search-form__last-search">
									<LastSearchContainer
										websiteType={WEBSITE_TUNNEL_TYPES.HOLIDAYS}
										updateFloatingPositionReference={
											updateFloatingPositionReference
										}
										onFocus={onFieldFocus}
									/>
								</div>
							</div>
							<div className="search-form__first-line-break" />
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

SmartDPSearchForm.propTypes = {
	onSuccess: PropTypes.func,
	departureCities: PropTypes.array,
	destinations: PropTypes.array,
	cabins: PropTypes.array,
	initialValues: PropTypes.object,
	topDestinationsList: PropTypes.array,
	usePortalForMenu: PropTypes.bool,
	validateOnMount: PropTypes.bool,
	handleFormError: PropTypes.func,
	onFieldFocus: PropTypes.func,
	isAutoScrollActive: PropTypes.bool,
	displayLoader: PropTypes.bool,
	brand: brandPropTypes,
	errorScrollIntoView: PropTypes.bool,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default memo(SmartDPSearchForm);
